import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Card, Container, Stack, Typography } from '@mui/material';

import { api } from 'src/api/api-client.config';
import Iconify from 'src/components/iconify';
import { LanguageTabs, TabPanel } from 'src/components/language-tabs';

// Import React-Quill and its styles
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  languages,
  quillFormats,
  quillModules,
  visibleLanguages,
} from '../config/quill-text-editor-settings';
import { useAppSearchParams } from '@/hooks/useAppSearchParams.ts';

// Initialize default content state
const initialState: Record<string, string> = languages.reduce((acc: Record<string, string>, lang) => {
  acc[`content_${lang}`] = ''; // Now using string for HTML content
  return acc;
}, {});

export default function PageEditPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<Record<string, string>>(initialState);
  const [value, setValue] = useState(0);

  const { pageId } = useAppSearchParams();

  useEffect(() => {
    if (!pageId) return;

    api.get(`/admin/pages/${pageId}`).then((data) => {
      setState(initialState);
      for (let lang of languages) {
        // Just use the HTML content directly
        setState((prevState) => ({ ...prevState, [`content_${lang}`]: (data as any).page?.[`content_${lang}`] || '' }));
      }

    });
  }, []);

  const handleChangeTabs = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleChange = (content: string, lang: string) => {
    setState((prevState) => ({
      ...prevState,
      [`content_${lang}`]: content,
    }));
  };

  const saveHandler = () => {
    let page = {};
    for (let lang of languages) {
      // Already in HTML format, no conversion needed
      (page as any)[`content_${lang}`] = state[`content_${lang}`];
    }

    setLoading(true);

    api
      .put(`/admin/pages/${pageId}`, page)
      .then(() => {
        navigate(`/dashboard/pages`);
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Helmet>
        <title> Page Edit </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            PageEdit
          </Typography>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:save-outline' />}
            onClick={() => saveHandler()}
            disabled={loading}
            sx={{ mr: 2 }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Stack>

        <LanguageTabs value={value} onChange={handleChangeTabs} />
        {visibleLanguages.map((lang, index) => (
          <TabPanel key={lang.code} value={value} index={index}>
            <Card sx={{ p: 1 }}>
              <ReactQuill
                value={state[`content_${lang.code}`]}
                onChange={(content) => handleChange(content, lang.code)}
                modules={quillModules}
                formats={quillFormats}
                style={{ height: '400px' }}
              />
            </Card>
          </TabPanel>
        ))}
      </Container>
    </>
  );
}
